import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Row, TextField } from 'seagull';
import { RichTextEditor } from 'seagull/components/RichTextEditor/RichTextEditor';
import { getTestIds } from 'seagull/utils/TestIds';
import { DisplayScopeCheckbox } from '~/components/DisplayScopeCheckbox/DisplayScopeCheckbox';
import { useOverlayContext, useStore } from '~/contexts';
import { useCreateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useCreateCustomPageTemplate';
import { useSaveCustomPageText } from '~/hooks/reactQuery/mutations/useSaveCustomPageText';
import { useTemplateSaveErrorHandler } from '~/hooks/reactQuery/mutations/useTemplateSaveErrorHandler';
import { useUpdateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useUpdateCustomPageTemplate';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useCustomPageText } from '~/hooks/reactQuery/queries/useCustomPageText';
import { useLocaleKeys } from '~/i18n';
import { isDuplicateTemplateName } from '~/models/custom-page-template/isDuplicateTemplateName';
import { selectors } from '~/store/state-selectors';
import s from './TextTemplateFormModal.module.scss';
export const GetTextTemplateFormModalTID = (parentTestId)=>getTestIds(parentTestId);
const serializeEditorContent = (content)=>content ? JSON.stringify(content) : '';
export const TextTemplateForm = (param)=>{
    let { mode, template, onBack, onSaved } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetTextTemplateFormModalTID(), []);
    const businessId = useStore(selectors.user.businessId);
    const { showErrorToast } = useOverlayContext();
    const { templates: existingTemplates } = useCustomPageTemplates();
    const [name, setName] = useState(mode === 'edit' && template ? template.name : '');
    const [pageTitle, setPageTitle] = useState(mode === 'edit' && template ? template.pageTitle : '');
    const [displayScope, setDisplayScope] = useState(mode === 'edit' && template ? template.displayScope : 'proposalOnly');
    const editorContentRef = useRef(undefined);
    const [nameError, setNameError] = useState(undefined);
    const [pageTitleError, setPageTitleError] = useState(undefined);
    const { content: loadedContent, isLoading: isLoadingContent, isError: isLoadContentError } = useCustomPageText({
        textId: mode === 'edit' ? template === null || template === void 0 ? void 0 : template.text.textId : undefined
    });
    useEffect(()=>{
        if (isLoadContentError) {
            showErrorToast(t.customPages.templates.loadContentError(), testIds.TextTemplateFormModalTestIds.LoadContentErrorToast());
            onBack();
        }
    }, [
        isLoadContentError,
        onBack,
        showErrorToast,
        t,
        testIds
    ]);
    const { saveText, isLoading: isSavingText } = useSaveCustomPageText();
    const { createTemplate, isLoading: isCreateLoading } = useCreateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const { updateTemplate, isLoading: isUpdateLoading } = useUpdateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const handleSaveError = useTemplateSaveErrorHandler({
        mode,
        setNameError
    });
    const isLoading = isCreateLoading || isUpdateLoading || isSavingText;
    const handleEditorChange = useCallback((value)=>{
        editorContentRef.current = value;
    }, []);
    const validate = ()=>{
        let valid = true;
        const trimmedName = name.trim();
        if (trimmedName === '') {
            setNameError(t.customPages.templates.fieldRequired());
            valid = false;
        } else if (isDuplicateTemplateName(trimmedName, existingTemplates, template === null || template === void 0 ? void 0 : template.id)) {
            setNameError(t.customPages.templates.duplicateNameMessage());
            valid = false;
        } else {
            setNameError(undefined);
        }
        if (pageTitle.trim() === '') {
            setPageTitleError(t.customPages.templates.fieldRequired());
            valid = false;
        } else {
            setPageTitleError(undefined);
        }
        return valid;
    };
    const persistEditorContent = async ()=>{
        try {
            const result = await saveText({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                content: serializeEditorContent(editorContentRef.current)
            });
            return result.textId;
        } catch (unused) {
            showErrorToast(t.customPages.templates.saveTextError(), testIds.TextTemplateFormModalTestIds.SaveTextErrorToast());
            return undefined;
        }
    };
    const handleSave = async ()=>{
        if (isLoading) return;
        if (!validate()) return;
        const textId = await persistEditorContent();
        if (textId == null) return;
        const payload = {
            type: 'text',
            name: name.trim(),
            pageTitle: pageTitle.trim(),
            displayScope,
            text: {
                textId
            }
        };
        if (mode === 'create') {
            createTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                data: payload
            }, {
                onError: handleSaveError
            });
        } else if (template) {
            updateTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                templateId: template.id,
                data: payload
            }, {
                onError: handleSaveError
            });
        }
    };
    const handleNameChange = (e)=>{
        setName(e.target.value);
        if (nameError) setNameError(undefined);
    };
    const handlePageTitleChange = (e)=>{
        setPageTitle(e.target.value);
        if (pageTitleError) setPageTitleError(undefined);
    };
    const handleCheckboxChange = ()=>{
        setDisplayScope((prev)=>prev === 'proposalOnly' ? 'proposalAndAgreement' : 'proposalOnly');
    };
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.TextTemplateFormModalTestIds.Root(),
        className: s.formContent,
        children: [
            /*#__PURE__*/ _jsx(TextField, {
                testId: testIds.TextTemplateFormModalTestIds.NameField(),
                label: t.customPages.templates.nameLabel(),
                value: name,
                onChange: handleNameChange,
                error: nameError != null,
                helperText: nameError,
                bi: {
                    name: 'text_template_form_name'
                }
            }),
            /*#__PURE__*/ _jsx(TextField, {
                testId: testIds.TextTemplateFormModalTestIds.PageTitleField(),
                label: t.customPages.templates.pageTitleLabel(),
                value: pageTitle,
                onChange: handlePageTitleChange,
                error: pageTitleError != null,
                helperText: pageTitleError,
                bi: {
                    name: 'text_template_form_page_title'
                }
            }),
            !isLoadingContent && /*#__PURE__*/ _jsx(RichTextEditor, {
                testId: testIds.TextTemplateFormModalTestIds.RichTextEditor(),
                initialValue: loadedContent !== null && loadedContent !== void 0 ? loadedContent : '',
                onChange: handleEditorChange,
                editorHeight: 300,
                bi: {
                    name: 'text_template_form_editor'
                }
            }),
            /*#__PURE__*/ _jsx(DisplayScopeCheckbox, {
                testId: testIds.TextTemplateFormModalTestIds.DisplayScopeCheckbox(),
                checked: displayScope === 'proposalAndAgreement',
                onChange: handleCheckboxChange
            }),
            /*#__PURE__*/ _jsxs(Row, {
                className: s.formFooter,
                justifyContent: "center",
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        onClick: onBack,
                        testId: testIds.TextTemplateFormModalTestIds.CancelButton(),
                        bi: {
                            name: 'text_template_form_cancel'
                        },
                        children: t.customPages.templates.cancel()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primary",
                        loading: isLoading,
                        onClick: handleSave,
                        testId: testIds.TextTemplateFormModalTestIds.SaveButton(),
                        bi: {
                            name: 'text_template_form_save'
                        },
                        children: t.customPages.templates.save()
                    })
                ]
            })
        ]
    });
};
